/*
 * <product-recommendations>
 * -------------------------
 * Fills the "You may also like" grid with dynamic Shopify product
 * recommendations for the product being viewed.
 *
 * Shopify only populates the `recommendations` Liquid object when the section is
 * re-rendered through the Product Recommendations API, so the grid is empty on
 * first paint. This element lazily fetches its own section via `data-url`
 * (.../recommendations/products?section_id=...&product_id=...&intent=related),
 * pulls the freshly-rendered <product-recommendations> out of the response and
 * swaps its inner HTML in. The whole grid — including <grid-slider> — is
 * replaced, so the fresh slider self-initialises.
 *
 * The heading ‹ › arrows live OUTSIDE this element; they re-bind to the new row
 * via the bubbling `theme:recommendations:loaded` event (see section-collection).
 *
 * The initial paint is empty; this element fetches its section through the
 * Product Recommendations API (data-url) and swaps in the result in one shot —
 * Shopify's related products, or the fallback collection the section renders when
 * there are none. So the grid goes straight from loading to its final content (no
 * fallback-then-related flash). If the request fails or returns nothing, the
 * section is hidden.
 */
(function () {
  'use strict';

  if (customElements.get('product-recommendations')) return;

  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;

        const url = this.getAttribute('data-url');
        if (!url) return; // keep the server-rendered fallback collection

        const observer = new IntersectionObserver(
          (entries, obs) => {
            if (!entries[0].isIntersecting) return;
            obs.unobserve(this);
            this.load(url);
          },
          { rootMargin: '0px 0px 400px 0px' }
        );
        observer.observe(this);
      }

      load(url) {
        this.loaded = true;

        fetch(url)
          .then((response) => (response.ok ? response.text() : Promise.reject(response.status)))
          .then((text) => {
            const fresh = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('product-recommendations');

            // The re-render returns the FINAL content in one shot — Shopify's
            // related products, or the fallback collection when there are none —
            // so we swap once (no fallback-then-related flash, no retry).
            if (fresh && fresh.querySelector('[data-grid-item]')) {
              this.innerHTML = fresh.innerHTML;
              this.removeAttribute('data-loading');
              this.dispatchEvent(new CustomEvent('theme:recommendations:loaded', { bubbles: true }));
            } else {
              this.hideSection();
            }
          })
          .catch(() => this.hideSection());
      }

      hideSection() {
        const section =
          this.closest('.shopify-section') || this.closest('[data-section-type="product-grid"]') || this;
        section.hidden = true;
      }
    }
  );
})();
